import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  Button,
  Input,
  Badge,
  StatCard,
  Muted,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  AlertBanner,
  FormGroup,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface SaleLine {
  id: string;
  product_name: string;
  format_name: string;
  qty: number;
  cash_price_at_sale: number;
  card_price_at_sale: number;
  pricing_tier_used: string;
  payment_method: string;
  returned?: boolean;
}

// Positive amounts, priced by the server (POST /sales/return/preview)
interface ReturnAmounts {
  subtotal_cash: number;
  subtotal_card: number;
  tax_cash: number;
  tax_card: number;
  refund: number;
}

interface Sale {
  id: string;
  created_at: string;
  payment_method: string;
  pricing_tier_used?: string;
  status: string;
  lines: SaleLine[];
  total: number;
}

interface RecentSale {
  id: string;
  receipt_number: string;
  completed_at: string;
  status: string;
  payment_method: string;
  card_brand: string | null;
  card_last4: string | null;
  actual_amount_paid: string | number | null;
  cashier: string | null;
  line_count: number;
  items: string | null;
}

type Step = "lookup" | "select" | "confirm" | "success";

interface CardRefund {
  status: "sending" | "pending" | "approved" | "declined" | "timeout" | "error";
  message?: string;
  last4?: string;
  attemptId?: string;
}

const isCardSale = (s: Sale | null) => !!s && (s.payment_method === "credit" || s.payment_method === "debit");

// ============================================================
// COMPONENT
// ============================================================

export default function Returns() {
  const [step, setStep] = useState<Step>("lookup");
  const [saleIdInput, setSaleIdInput] = useState("");
  const [sale, setSale] = useState<Sale | null>(null);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refundTotal, setRefundTotal] = useState(0);
  const [preview, setPreview] = useState<ReturnAmounts | null>(null);
  const [helcimRefundId, setHelcimRefundId] = useState("");
  const [markingRefund, setMarkingRefund] = useState(false);
  const [refundMarked, setRefundMarked] = useState(false);
  // The return sale, and its refund on the terminal (Paula 10/29: a debit
  // return still had to be voided by hand on Helcim's website).
  const [refundSaleId, setRefundSaleId] = useState<string | null>(null);
  const [cardRefund, setCardRefund] = useState<CardRefund | null>(null);

  // ----------------------------------------------------------
  // Recent sales (Donovan, 22 Oct 2026: there was no transaction id to type)
  // ----------------------------------------------------------

  const [recent, setRecent] = useState<RecentSale[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const loadRecent = useCallback(async (q: string) => {
    setRecentLoading(true);
    try {
      const res = await api.get("/sales/recent", { params: q.trim() ? { q: q.trim() } : {} });
      setRecent(res.data || []);
    } catch {
      setRecent([]);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step !== "lookup") return;
    const t = setTimeout(() => loadRecent(saleIdInput), 250);
    return () => clearTimeout(t);
  }, [saleIdInput, step, loadRecent]);

  // ----------------------------------------------------------
  // Lookup sale
  // ----------------------------------------------------------

  const lookupSale = async (idArg?: string) => {
    let trimmed = (idArg ?? saleIdInput).trim();
    if (!trimmed) return;
    // A receipt number (first 8 of the id) or any search that finds exactly one sale.
    if (!/^[0-9a-f-]{36}$/i.test(trimmed)) {
      if (recent.length === 1) trimmed = recent[0].id;
      else {
        setError(recent.length ? "More than one sale matches. Pick it from the list below." : "No sale matches that.");
        return;
      }
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/sales/${trimmed}`);
      const saleData: Sale = res.data;
      if (!saleData || !saleData.lines || saleData.lines.length === 0) {
        setError("Sale found but has no line items.");
        setLoading(false);
        return;
      }
      setSale(saleData);
      setSelectedLineIds(new Set());
      setStep("select");
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error || "Sale not found."
          : "Failed to look up sale. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // Toggle line selection
  // ----------------------------------------------------------

  const toggleLine = (lineId: string) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  };

  const returnable = sale ? sale.lines.filter((l) => !l.returned) : [];

  const toggleAll = () => {
    if (!sale) return;
    if (selectedLineIds.size === returnable.length) {
      setSelectedLineIds(new Set());
    } else {
      setSelectedLineIds(new Set(returnable.map((l) => l.id)));
    }
  };

  // ----------------------------------------------------------
  // Calculate refund
  // ----------------------------------------------------------

  // Donovan's 10/29 test: this used to add up shelf prices, so a $1.25 cash
  // sale offered $1.18 back with the tax left off. The server prices it now,
  // the same way it records it: tax, discounts and card markup included.
  useEffect(() => {
    setPreview(null);
    if (step !== "select" || !sale || selectedLineIds.size === 0) return;
    let cancelled = false;
    api
      .post("/sales/return/preview", {
        original_sale_id: sale.id,
        line_ids: Array.from(selectedLineIds),
      })
      .then((res) => { if (!cancelled) { setPreview(res.data); setError(""); } })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined;
        setError(msg || "Couldn't work out the refund. Try again.");
      });
    return () => { cancelled = true; };
  }, [step, sale, selectedLineIds]);

  const isCardTier = (sale?.pricing_tier_used ?? sale?.payment_method) === "card" || sale?.payment_method === "credit";
  const itemsAmount = preview ? (isCardTier ? preview.subtotal_card : preview.subtotal_cash) : 0;
  const taxAmount = preview ? (isCardTier ? preview.tax_card : preview.tax_cash) : 0;
  // Gift card and split sales don't split cleanly into items + tax; show the total only.
  const showBreakdown = preview !== null && Math.abs(itemsAmount + taxAmount - preview.refund) < 0.005;

  const goToConfirm = () => {
    if (selectedLineIds.size === 0 || !preview) return;
    setRefundTotal(preview.refund);
    setStep("confirm");
  };

  // ----------------------------------------------------------
  // Process return
  // ----------------------------------------------------------

  const processReturn = async () => {
    if (!sale) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/sales/return", {
        original_sale_id: sale.id,
        line_ids: Array.from(selectedLineIds),
        register_id: "register-1",
      });
      if (typeof res.data?.refund === "number") setRefundTotal(res.data.refund);
      setRefundSaleId(res.data?.id ?? null);
      setStep("success");
      if (isCardSale(sale) && res.data?.id) refundToCard(res.data.id);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error || "Failed to process return."
          : "Failed to process return. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // Refund to the card on the terminal
  // ----------------------------------------------------------

  const apiError = (err: unknown, fallback: string) =>
    (err && typeof err === "object" && "response" in err
      ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
      : undefined) || fallback;

  const showRefund = (r: { status: string; processorResponse?: string; last4?: string; attemptId?: string }) => {
    const status = (["pending", "approved", "declined", "timeout"].includes(r.status) ? r.status : "error") as CardRefund["status"];
    setCardRefund({ status, message: r.processorResponse, last4: r.last4, attemptId: r.attemptId });
    if (status === "approved") setRefundMarked(true);
  };

  const refundToCard = async (id: string) => {
    setCardRefund({ status: "sending" });
    try {
      const res = await api.post("/payments/return-refund", { refund_sale_id: id });
      showRefund(res.data);
    } catch (err: unknown) {
      setCardRefund({ status: "error", message: apiError(err, "Couldn't send the refund to the terminal.") });
    }
  };

  // While the terminal waits for the card, check every 2 seconds.
  useEffect(() => {
    if (cardRefund?.status !== "pending" || !cardRefund.attemptId) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/payments/refund-attempts/${cardRefund.attemptId}`);
        showRefund(res.data);
      } catch (err: unknown) {
        setCardRefund({ status: "error", message: apiError(err, "Lost track of the refund. Check the Helcim dashboard.") });
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [cardRefund]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------
  // Reset
  // ----------------------------------------------------------

  const reset = () => {
    setStep("lookup");
    setSaleIdInput("");
    setSale(null);
    setSelectedLineIds(new Set());
    setError("");
    setRefundTotal(0);
    setPreview(null);
    setHelcimRefundId("");
    setMarkingRefund(false);
    setRefundMarked(false);
    setRefundSaleId(null);
    setCardRefund(null);
  };

  // ----------------------------------------------------------
  // Mark Helcim refund as completed
  // ----------------------------------------------------------

  const markRefundDone = async () => {
    if (!sale || !helcimRefundId.trim()) return;
    setMarkingRefund(true);
    try {
      await api.post(`/payments/${refundSaleId ?? sale.id}/refund-completed`, {
        helcim_refund_id: helcimRefundId.trim(),
      });
      setRefundMarked(true);
    } catch {
      setError("Failed to record refund completion. Try again.");
    } finally {
      setMarkingRefund(false);
    }
  };

  // ----------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------

  const fmt = (n: number) => `$${n.toFixed(2)}`;

  const paymentBadgeVariant = (method: string): "green" | "blue" | "default" => {
    if (method === "cash") return "green";
    if (method === "card") return "blue";
    return "default";
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <PageContainer>
      <PageHeader
        title="Returns / Refunds"
        subtitle="Process returns against completed sales"
        backTo="/register"
      />

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}

      {/* ------ STEP 1: LOOKUP ------ */}
      {step === "lookup" && (
        <Card>
          <CardHeader title="Look Up Original Sale" />
          <div className="max-w-md">
            <FormGroup label="Find the sale">
              <div className="flex gap-3">
                <Input
                  value={saleIdInput}
                  onChange={setSaleIdInput}
                  placeholder="Receipt #, card last 4, amount, or product"
                />
                <Button onClick={() => lookupSale()} disabled={loading || !saleIdInput.trim()}>
                  {loading ? "Searching..." : "Look Up"}
                </Button>
              </div>
            </FormGroup>
            <div className="mt-2">
              <Muted>Or tap the sale in the list. Newest first, last 30 days.</Muted>
            </div>
          </div>
          <div className="mt-4">
            {recentLoading && recent.length === 0 ? (
              <Muted>Loading sales...</Muted>
            ) : recent.length === 0 ? (
              <Muted>No sales match.</Muted>
            ) : (
              <Table>
                <Thead>
                  <Th>When</Th>
                  <Th>Receipt #</Th>
                  <Th>Items</Th>
                  <Th>Paid by</Th>
                  <Th>Total</Th>
                  <Th>Cashier</Th>
                </Thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} onClick={() => (r.status === "voided" ? undefined : lookupSale(r.id))}
                      style={{ cursor: r.status === "voided" ? "default" : "pointer", borderTop: "1px solid var(--rbw-border)" }}>
                      <Td>{new Date(r.completed_at).toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}</Td>
                      <Td>{r.receipt_number}</Td>
                      <Td>
                        {(r.items || "").length > 60 ? (r.items || "").slice(0, 60) + "..." : r.items}
                        {r.status === "voided" && <> <Badge variant="danger">voided</Badge></>}
                      </Td>
                      <Td>{(r.payment_method || "").toUpperCase()}{r.card_last4 ? ` ...${r.card_last4}` : ""}</Td>
                      <Td>{fmt(Number(r.actual_amount_paid || 0))}</Td>
                      <Td>{r.cashier}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Card>
      )}

      {/* ------ STEP 2: SELECT ITEMS ------ */}
      {step === "select" && sale && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard
              label="Sale ID"
              value={sale.id.slice(0, 8) + "..."}
              detail={new Date(sale.created_at).toLocaleString()}
            />
            <StatCard
              label="Payment Method"
              value={sale.payment_method.toUpperCase()}
              variant={sale.payment_method === "cash" ? "green" : "blue"}
            />
            <StatCard
              label="Original Total"
              value={fmt(Number((sale as unknown as { actual_amount_paid?: number }).actual_amount_paid ?? sale.total ?? 0))}
              variant="gold"
            />
          </div>

          <Card padding="p-0">
            <div className="p-6 pb-0">
              <CardHeader
                title="Select Items to Return"
                action={
                  <Button variant="ghost" size="sm" onClick={toggleAll}>
                    {selectedLineIds.size === returnable.length ? "Deselect All" : "Select All"}
                  </Button>
                }
              />
            </div>

            <Table>
              <Thead>
                <Th> </Th>
                <Th>Product</Th>
                <Th>Format</Th>
                <Th align="center">Qty</Th>
                <Th>Pricing Tier</Th>
                <Th align="right">Unit Price</Th>
                <Th align="right">Line Total</Th>
              </Thead>
              <tbody>
                {sale.lines.map((line, idx) => {
                  const unitPrice =
                    sale.payment_method === "cash"
                      ? line.cash_price_at_sale
                      : line.card_price_at_sale;
                  const lineTotal = unitPrice * line.qty;
                  const checked = selectedLineIds.has(line.id);

                  return (
                    <TRow key={line.id} index={idx}>
                      <Td>
                        {line.returned ? (
                          <Badge variant="danger">Returned</Badge>
                        ) : (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLine(line.id)}
                            className="w-4 h-4 rounded cursor-pointer"
                            style={{ accentColor: "var(--rbw-gold)" }}
                          />
                        )}
                      </Td>
                      <Td bold>{line.product_name}</Td>
                      <Td>{line.format_name}</Td>
                      <Td align="center">{line.qty}</Td>
                      <Td>
                        <Badge variant={paymentBadgeVariant(sale.payment_method)}>
                          {line.pricing_tier_used}
                        </Badge>
                      </Td>
                      <Td align="right">{fmt(unitPrice)}</Td>
                      <Td align="right" bold>{fmt(lineTotal)}</Td>
                    </TRow>
                  );
                })}
              </tbody>
            </Table>
          </Card>

          {selectedLineIds.size > 0 && (
            <Card className="mt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>
                    Refund Amount ({selectedLineIds.size} item{selectedLineIds.size !== 1 ? "s" : ""})
                  </div>
                  <div className="text-3xl font-bold mt-1" style={{ color: "var(--rbw-danger)" }}>
                    {preview ? fmt(preview.refund) : "..."}
                  </div>
                  {showBreakdown && (
                    <div className="text-xs mt-1">
                      <Muted>Includes {fmt(taxAmount)} tax</Muted>
                    </div>
                  )}
                  <div className="text-xs mt-1">
                    <Muted>
                      Refund via {sale.payment_method.toUpperCase()} (original payment method)
                    </Muted>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="ghost" onClick={reset}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={goToConfirm} disabled={!preview}>
                    Continue to Confirm
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {selectedLineIds.size === 0 && (
            <div className="mt-6 text-center">
              <Muted>Select at least one item to process a return.</Muted>
              <div className="mt-3">
                <Button variant="ghost" onClick={reset}>
                  Back to Lookup
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ------ STEP 3: CONFIRM ------ */}
      {step === "confirm" && sale && (
        <Card>
          <CardHeader title="Confirm Return" />

          <AlertBanner variant="gold">
            Please review the return details below before confirming. This action cannot be undone.
          </AlertBanner>

          <div className="space-y-4 mb-6">
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--rbw-gray-dark)" }}>Original Sale</span>
              <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{sale.id}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--rbw-gray-dark)" }}>Payment Method</span>
              <Badge variant={paymentBadgeVariant(sale.payment_method)}>
                {sale.payment_method.toUpperCase()}
              </Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--rbw-gray-dark)" }}>Items Being Returned</span>
              <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                {selectedLineIds.size} of {sale.lines.length}
              </span>
            </div>

            <div className="my-4" style={{ borderTop: "1px solid var(--rbw-border)" }} />

            {sale.lines
              .filter((l) => selectedLineIds.has(l.id))
              .map((line) => (
                <div key={line.id} className="flex justify-between text-sm">
                  <span style={{ color: "var(--rbw-navy)" }}>
                    {line.product_name} ({line.format_name}) x{line.qty}
                  </span>
                </div>
              ))}

            {showBreakdown && (
              <>
                <div className="my-4" style={{ borderTop: "1px solid var(--rbw-border)" }} />
                <div className="flex justify-between text-sm">
                  <span style={{ color: "var(--rbw-gray-dark)" }}>Items</span>
                  <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{fmt(itemsAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: "var(--rbw-gray-dark)" }}>Tax</span>
                  <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{fmt(taxAmount)}</span>
                </div>
              </>
            )}

            <div className="my-4" style={{ borderTop: "2px solid var(--rbw-border)" }} />

            <div className="flex justify-between">
              <span className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
                Total Refund
              </span>
              <span className="text-2xl font-bold" style={{ color: "var(--rbw-danger)" }}>
                {fmt(refundTotal)}
              </span>
            </div>
          </div>

          {(sale.payment_method === "credit" || sale.payment_method === "debit") && (
            <AlertBanner variant="blue">
              Confirming sends the refund to the card terminal. Have the customer ready to tap or
              insert the same card they paid with.
            </AlertBanner>
          )}

          {error && <AlertBanner variant="danger">{error}</AlertBanner>}

          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={() => setStep("select")} disabled={loading}>
              Back
            </Button>
            <Button variant="danger" onClick={processReturn} disabled={loading}>
              {loading ? "Processing..." : "Confirm Return"}
            </Button>
          </div>
        </Card>
      )}

      {/* ------ STEP 4: SUCCESS ------ */}
      {step === "success" && (
        <Card>
          <div className="text-center py-8">
            <div className="text-5xl mb-4">&#10003;</div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
              Return Processed Successfully
            </h2>
            <p className="text-sm mb-6" style={{ color: "var(--rbw-gray-dark)" }}>
              Inventory has been restored. {sale?.payment_method === "cash" ? "Cash refund ready." : ""}
            </p>

            <StatCard
              label="Refund Amount"
              value={fmt(refundTotal)}
              detail={`Via ${sale?.payment_method?.toUpperCase() ?? "original method"}`}
              variant="green"
            />

            {isCardSale(sale) && cardRefund?.status === "approved" && (
              <div className="mt-6">
                <AlertBanner variant="green">
                  Refunded {fmt(refundTotal)} to the card{cardRefund.last4 ? ` ending ${cardRefund.last4}` : ""}.
                </AlertBanner>
              </div>
            )}

            {isCardSale(sale) && (cardRefund?.status === "sending" || cardRefund?.status === "pending") && (
              <div className="mt-6">
                <AlertBanner variant="gold">
                  Sent {fmt(refundTotal)} to the terminal. Have the customer tap or insert the same card.
                </AlertBanner>
              </div>
            )}

            {isCardSale(sale) && cardRefund && ["declined", "timeout", "error"].includes(cardRefund.status) && (
              <div className="mt-6 rounded-xl p-5 text-left"
                style={{ background: "#FEF2F2", border: "2px solid #FECACA" }}>
                <div className="font-bold text-sm mb-1" style={{ color: "#DC2626" }}>
                  The card refund didn't go through
                </div>
                <div className="text-sm" style={{ color: "#991B1B" }}>
                  {cardRefund.message || "The terminal didn't refund the card."} The return is recorded;
                  the customer's money is not back yet.
                </div>
                <div className="flex gap-2 mt-3">
                  {cardRefund.status !== "timeout" && refundSaleId && (
                    <Button variant="danger" size="sm" onClick={() => refundToCard(refundSaleId)}>
                      Send to Terminal Again
                    </Button>
                  )}
                </div>
                <div className="mt-3 pt-3" style={{ borderTop: "1px solid #FECACA" }}>
                  <div className="text-xs mb-2" style={{ color: "#991B1B" }}>
                    Or refund it on the Helcim dashboard (Transactions &gt; the original charge &gt; Refund
                    for {fmt(refundTotal)}) and enter Helcim's refund ID here:
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Helcim refund/txn ID"
                      value={helcimRefundId}
                      onChange={(e) => setHelcimRefundId(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ border: "1px solid #FECACA", color: "var(--rbw-navy)" }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={markRefundDone}
                      disabled={!helcimRefundId.trim() || markingRefund}
                    >
                      {markingRefund ? "Saving..." : "Mark Done"}
                    </Button>
                  </div>
                  {refundMarked && (
                    <div className="text-xs mt-2 font-medium" style={{ color: "var(--rbw-success)" }}>
                      Refund confirmed and recorded.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8">
              <Button onClick={reset}>Process Another Return</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Empty state when nothing is happening yet — only show on lookup with no input */}
    </PageContainer>
  );
}
